(() => {
  if (customElements.get('sticky-add-to-cart')) return;

  class StickyAddToCart extends HTMLElement {
    constructor() {
      super();

      this.sectionId = this.dataset.sectionId;
      this.productInfo =
        document.querySelector(`product-info[data-section="${this.sectionId}"]`) ||
        document.querySelector(`#MainProduct-${this.sectionId}`) ||
        document.querySelector('product-info');

      this.qtyInput = this.querySelector('.sticky-atc__qty-input');
      this.priceEl = this.querySelector('.sticky-atc__price');
      this.compareEl = this.querySelector('.sticky-atc__compare');
      this.imageEl = this.querySelector('.sticky-atc__image');
      this.buttonEl = this.querySelector('.sticky-atc__button');
      this.plusBtn = this.querySelector('[data-sticky-qty="plus"]');
      this.minusBtn = this.querySelector('[data-sticky-qty="minus"]');
      this.optionEls = [...this.querySelectorAll('.sticky-atc__variant-option')];
      this.mobileDetailsEl = this.querySelector('.sticky-atc__mobile-details');
      this.buttonTextEl = this.querySelector('.sticky-atc__button span');

      this.variantData = this.getVariantData();
      this.mainForm = null;
      this.variantInput = null;
      this.quantityInput = null;
      this.submitButton = null;
      this.visibilityObserver = null;
      this.mutationObserver = null;
      this.isSubmitting = false;

      this.onDocumentChange = this.onDocumentChange.bind(this);
      this.onDocumentInput = this.onDocumentInput.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.refreshTargets();

      if (!this.mainForm || !this.variantInput || !this.buttonEl) {
        return;
      }

      this.bindEvents();
      this.syncFromForm();
      this.setupObserver();
      this.observeProductInfo();
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onDocumentChange);
      document.removeEventListener('input', this.onDocumentInput);
      window.removeEventListener('resize', this.onResize);

      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();

      document.body.style.paddingBottom = '';
    }

    getVariantData() {
      const script = this.querySelector('.sticky-atc__variants');
      if (!script) return [];

      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        return [];
      }
    }

    refreshTargets() {
      const scope = this.productInfo || document;

      this.mainForm =
        scope.querySelector('product-form form[data-type="add-to-cart-form"]') ||
        scope.querySelector('form[data-type="add-to-cart-form"]') ||
        scope.querySelector('product-form form') ||
        null;

      this.variantInput = this.mainForm?.querySelector('input[name="id"]') || null;
      this.quantityInput = this.mainForm?.querySelector('input[name="quantity"]') || this.productInfo?.querySelector('input[name="quantity"]') || null;
      this.submitButton =
        this.mainForm?.querySelector('button[name="add"]') ||
        this.mainForm?.querySelector('.product-form__submit') ||
        null;
    }

    bindEvents() {
      document.addEventListener('change', this.onDocumentChange);
      document.addEventListener('input', this.onDocumentInput);
      window.addEventListener('resize', this.onResize, { passive: true });

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-sticky-edit]')) {
          event.preventDefault();
          const scope = document.getElementById(`MainProduct-${this.sectionId}`) || this.productInfo;
          if (!scope) return;
          const visible = (element) => element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
          const picker = [...scope.querySelectorAll('.swatch-view, variant-selects, .product-form__input')].find(visible)
            || scope.querySelector('.product__info-container') || scope;
          const header = document.querySelector('.shopify-section-header-sticky, .section-header');
          const offset = (header?.getBoundingClientRect().height || 0) + 20;
          window.scrollTo({ top: Math.max(0, window.scrollY + picker.getBoundingClientRect().top - offset), behavior: 'instant' });
          const control = [...picker.querySelectorAll('[role="radio"], select, input:not([type="hidden"]):not([disabled]), button:not([disabled])')].find(visible);
          if (control) control.focus({ preventScroll: true });
          else { picker.setAttribute('tabindex', '-1'); picker.focus({ preventScroll: true }); }
          return;
        }
        const qtyBtn = event.target.closest('[data-sticky-qty]');
        if (qtyBtn && this.qtyInput) {
          const current = parseInt(this.qtyInput.value || '1', 10);
          const delta = qtyBtn.dataset.stickyQty === 'plus' ? 1 : -1;
          this.setQuantity(current + delta);
          return;
        }

        if (event.target.closest('.sticky-atc__button')) {
          this.handleAddToCart();
        }
      });

      if (this.qtyInput) {
        this.qtyInput.addEventListener('input', () => {
          this.setQuantity(this.qtyInput.value);
        });

        this.qtyInput.addEventListener('change', () => {
          this.setQuantity(this.qtyInput.value);
        });
      }
    }

    onDocumentChange(event) {
      if (!this.productInfo || !this.productInfo.contains(event.target)) return;

      requestAnimationFrame(() => {
        this.refreshTargets();
        this.syncFromForm();
        this.setupObserver();
      });
    }

    onDocumentInput(event) {
      if (!this.productInfo || !this.productInfo.contains(event.target)) return;

      if (event.target.name === 'quantity') {
        this.syncQuantityFromForm();
      }
    }

    onResize() {
      requestAnimationFrame(() => {
        this.setupObserver();
        this.updateBodyOffset();
      });
    }

    observeProductInfo() {
      if (!this.productInfo) return;

      this.mutationObserver = new MutationObserver((records) => {
        if (records.every(record => this.contains(record.target))) return;
        requestAnimationFrame(() => {
          this.refreshTargets();
          this.syncFromForm();
          this.setupObserver();
        });
      });

      this.mutationObserver.observe(this.productInfo, {
        attributes: true,
        attributeFilter: ['value', 'disabled', 'class', 'aria-busy'],
        childList: true,
        subtree: true
      });
    }

    setupObserver() {
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      if (!this.submitButton) return;

      const header = document.querySelector('.shopify-section-header-sticky, .section-header');
      const headerOffset = header ? header.getBoundingClientRect().height : 0;

      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const variant = this.getCurrentVariant();
          const shouldShow = !!(variant && !entry.isIntersecting && entry.boundingClientRect.top < headerOffset);

          this.classList.toggle('is-visible', shouldShow);
          this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
          this.updateBodyOffset();
        },
        {
          root: null,
          threshold: 0.05,
          rootMargin: `-${headerOffset}px 0px 0px 0px`
        }
      );

      this.visibilityObserver.observe(this.submitButton);
    }

    updateBodyOffset() {
      const mobile = window.innerWidth < 750;
      document.body.style.paddingBottom =
        this.classList.contains('is-visible') ? `${Math.ceil(this.getBoundingClientRect().height)}px` : '';
    }

    getCurrentVariant() {
      const id = parseInt(this.variantInput?.value || '0', 10);
      if (!id) return null;

      return this.variantData.find((variant) => Number(variant.id) === id) || null;
    }

    getMaxAllowedQuantity(variant) {
      if (!variant) return null;

      const isTracked = !!variant.inventory_management;
      const denyOversell = variant.inventory_policy === 'deny';
      const qty = Number(variant.inventory_quantity);

      if (isTracked && denyOversell && Number.isFinite(qty)) {
        return Math.max(0, qty);
      }

      return null;
    }

    syncQuantityFromForm() {
      if (!this.qtyInput || !this.quantityInput) return;

      this.qtyInput.value = this.quantityInput.value || '1';
      this.updateQuantityButtonsState();
    }

    pushQuantity(value) {
      if (!this.quantityInput) return;

      this.quantityInput.value = String(value);
      this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    setQuantity(value) {
      const variant = this.getCurrentVariant();
      const maxAllowed = this.getMaxAllowedQuantity(variant);
      const baseMin = Math.max(
        1,
        parseInt(this.quantityInput?.min || this.qtyInput?.min || '1', 10) || 1
      );

      let normalized = Math.max(baseMin, parseInt(value || String(baseMin), 10) || baseMin);

      if (maxAllowed !== null) {
        normalized = Math.min(normalized, Math.max(1, maxAllowed));
      }

      if (this.qtyInput) {
        this.qtyInput.value = String(normalized);
      }

      this.pushQuantity(normalized);
      this.updateDisplayedPrices();
      this.updateQuantityButtonsState();
    }

    updateQuantityButtonsState() {
      const variant = this.getCurrentVariant();
      const maxAllowed = this.getMaxAllowedQuantity(variant);
      const currentQty = Math.max(1, parseInt(this.qtyInput?.value || '1', 10) || 1);

      if (this.minusBtn) {
        this.minusBtn.disabled = currentQty <= 1;
      }

      if (this.plusBtn) {
        this.plusBtn.disabled = maxAllowed !== null ? currentQty >= maxAllowed : false;
      }

      if (this.qtyInput) {
        if (maxAllowed !== null) {
          this.qtyInput.max = String(Math.max(1, maxAllowed));
        } else {
          this.qtyInput.removeAttribute('max');
        }
      }
    }

    updateDisplayedPrices() {
      const variant = this.getCurrentVariant();
      if (!variant) return;

      const quantity = Math.max(1, parseInt(this.qtyInput?.value || '1', 10) || 1);
      const totalPrice = (variant.price || 0) * quantity;
      const totalCompare = (variant.compare_at_price || 0) * quantity;

      if (this.priceEl) {
        this.priceEl.textContent = this.formatMoney(totalPrice);
      }

      if (this.compareEl) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        this.compareEl.textContent = onSale ? this.formatMoney(totalCompare) : '';
        this.compareEl.classList.toggle('hidden', !onSale);
      }
    }

    syncFromForm() {
      const variant = this.getCurrentVariant();
      if (!variant || this.variantInput?.disabled) {
        this.buttonEl.disabled = true;
        if (this.buttonTextEl) this.buttonTextEl.textContent = window.variantStrings?.unavailable || 'Unavailable';
        return;
      }

      this.syncQuantityFromForm();
      this.updateDisplayedPrices();

      if (this.imageEl && variant.featured_image?.src) {
        this.imageEl.src = variant.featured_image.src;
      }

      if (variant.options && this.optionEls.length) {
        this.optionEls.forEach((el, index) => {
          const strong = el.querySelector('strong');
          if (strong) {
            strong.textContent = variant.options[index] || '';
          }
        });
      }

      if (variant.options && this.mobileDetailsEl) {
        this.mobileDetailsEl.textContent = variant.options.filter(Boolean).join(' · ');
      }

      if (this.buttonEl) {
        const isSoldOut = !variant.available;
        const isMainButtonBusy =
          !!this.submitButton?.classList.contains('loading') ||
          this.submitButton?.getAttribute('aria-busy') === 'true';

        this.buttonEl.disabled = isSoldOut || this.isSubmitting || isMainButtonBusy || !!this.submitButton?.disabled;

        let nextLabel = 'Add to cart';

        if (isSoldOut) {
          nextLabel = 'Sold out';
        } else if (this.isSubmitting || isMainButtonBusy) {
          nextLabel = 'Loading...';
        } else {
          nextLabel =
            this.submitButton?.querySelector('[data-premium-atc-label]')?.textContent?.trim() ||
            'Add to cart';
        }

        if (this.buttonTextEl) {
          this.buttonTextEl.textContent = nextLabel;
        }
      }

      this.updateQuantityButtonsState();
    }

    async handleAddToCart() {
      const variant = this.getCurrentVariant();

      this.refreshTargets();
      if (!variant || !variant.available || !this.buttonEl || this.isSubmitting || this.submitButton?.disabled || this.submitButton?.classList.contains('loading') || this.variantInput?.disabled) return;

      this.isSubmitting = true;
      this.buttonEl.disabled = true;

      if (this.buttonTextEl) {
        this.buttonTextEl.textContent = 'Loading...';
      }

      try {
        this.refreshTargets();

        if (!this.mainForm) {
          throw new Error('Main product form not found');
        }

        if (this.quantityInput && this.qtyInput) {
          this.quantityInput.value = this.qtyInput.value || '1';
          this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
          this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (this.submitButton) {
          this.submitButton.click();
        } else if (typeof this.mainForm.requestSubmit === 'function') {
          this.mainForm.requestSubmit();
        } else {
          this.mainForm.submit();
        }
      } catch (error) {
        if (this.buttonTextEl) {
          this.buttonTextEl.textContent = error.message || 'Error';
        }

        this.isSubmitting = false;

        window.setTimeout(() => {
          this.syncFromForm();
        }, 1200);

        return;
      }

      window.setTimeout(() => {
        this.isSubmitting = false;
        this.syncFromForm();
      }, 1200);
    }

    formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(
          cents,
          this.dataset.moneyFormat || window.money_format || '{{amount}}'
        );
      }

      return (cents / 100).toFixed(2);
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
})();
